import type { components as OpenAPI } from "@/autogen/openapi";
import URLS from "@/autogen/urls";
import type { Newsletter } from "@/types/newsletter";

// --- Types ---

export type SubscriberType = OpenAPI["schemas"]["SubscriberType"];

export type SubscriptionState =
  | SubscriberType
  | "not_subscribed"
  | "subscribed_elsewhere"
  | "premium"
  | "pending_validation"
  | "pending_subscription"
  | "invalid_email"
  | "invalid_email__regex"
  | "invalid_ip_address"
  | "invalid_subscription"
  | "errored_email"
  | "pending_email_address_confirmation"
  | "awaiting_email_address_confirmation";

export type SubscribeFormVariant =
  | "subscribe_page"
  | "archive_page"
  | "paywalled_archive_page";

type AuthenticationStatus =
  | "unauthenticated"
  | "email_address_provided"
  | "email_address_confirmed"
  | "author_logged_in";

export type SubscriberFacingAuthentication = {
  status: AuthenticationStatus;
  email_address: string;
  subscribers: BasicSubscriber[];
};

export type BasicSubscriber = {
  id: string;
  email_address: string;
  type: SubscriptionState;
  newsletter_id: string;
};

export type FormSurvey = {
  id: string;
  question: string;
  answers: string[];
  input_type: "radio" | "checkbox" | "text";
};

// --- Helpers ---

// Cookies relevant for UTM tracking, should match `emails/middleware/utm.py`.
const RELEVANT_COOKIES = [
  "utm_campaign",
  "utm_content",
  "utm_medium",
  "utm_source",
  "utm_term",
];

const decomposeCookieString = (
  cookieString: string
): Record<string, string> => {
  return Object.fromEntries(
    cookieString
      .split("; ")
      .map((x) => x.split(/=(.*)$/, 2).map(decodeURIComponent))
  );
};

const fetchRelevantCookies = (): Record<string, string> => {
  const all = decomposeCookieString(document.cookie);
  return Object.fromEntries(
    Object.entries(all).filter(([key]) => RELEVANT_COOKIES.includes(key))
  );
};

export const parseError = (
  error: unknown,
  data?: any
): { state: SubscriptionState; error: string } => {
  if (error === undefined || error === null) {
    return {
      state: "errored_email",
      error: "Something went wrong. Please try again.",
    };
  }

  // Handle array-format validation errors from the API
  if (Array.isArray(error)) {
    const firstError = error[0] as
      | { loc?: string[]; msg?: string; type?: string }
      | undefined;
    if (firstError) {
      if (
        firstError.type === "value_error.str.regex" &&
        firstError.loc?.includes("email_address")
      ) {
        return {
          state: "invalid_email__regex",
          error: "That email address is invalid",
        };
      }
      if (firstError.msg) {
        return { state: "errored_email", error: firstError.msg };
      }
    }
  }

  const errorString = typeof error === "string" ? error : String(error);

  if (errorString === "Email already exists") {
    return {
      state: "subscribed_elsewhere",
      error: "This email address is already subscribed.",
    };
  }

  const riskScores = data?.risk_scores;
  if (!riskScores) {
    return { state: "errored_email", error: errorString };
  }

  const ipRiskScore = Object.entries(riskScores)
    .filter(([key]) => key.includes("ip_address_"))
    .reduce((acc, [, value]) => acc + (value as number), 0);

  if (ipRiskScore >= 0.5) {
    return {
      state: "invalid_ip_address",
      error: "That IP address is problematic",
    };
  }

  return { state: "errored_email", error: errorString };
};

export type SubscribeResult =
  | { subscriber: BasicSubscriber }
  | { error: string | object; data?: any };

export const subscribe = async (
  emailAddress: string,
  metadata: Record<string, string>,
  newsletter: Newsletter,
  referringSubscriberId: string | null,
  turnstileResponseToken: string | null,
  formId: string | null = null
): Promise<SubscribeResult> => {
  const searchParams = new URLSearchParams(location.search);
  const payload = {
    ...(referringSubscriberId
      ? { referring_subscriber_id: referringSubscriberId }
      : {}),
    ...fetchRelevantCookies(),
    ...Object.fromEntries(searchParams),
    metadata: Object.fromEntries(
      Object.entries(metadata).map(([key, value]) => [
        key,
        value === "on" ? "True" : value,
      ])
    ),
    tags: searchParams.getAll("tag"),
    email_address: emailAddress,
    newsletter_id: newsletter.id,
    turnstile_response_token: turnstileResponseToken,
    form_id: formId,
  };

  try {
    const response = await fetch(URLS["create-subscriber"](), {
      method: "POST",
      body: JSON.stringify(payload),
      headers: { "Content-Type": "application/json" },
    });
    const data = await response.json();

    if (data.detail || data.error) {
      return { error: data.detail || data.error, data };
    }

    const subscriber: BasicSubscriber = {
      id: data.subscriber.id,
      email_address: data.subscriber.email_address,
      type: data.subscriber.type,
      newsletter_id: newsletter.id,
    };

    if (window.plausible) {
      window.plausible("Subscribe");
    } else if (window.fathom && newsletter.fathom_subscribe_code) {
      window.fathom.trackGoal(newsletter.fathom_subscribe_code, 0);
    }

    return { subscriber };
  } catch (e: any) {
    return { error: e?.message || "Something went wrong.", data: null };
  }
};

export const mutableUrl = (
  newsletter: Newsletter,
  subscriber: { id: string } | undefined,
  route: string
): string | null => {
  if (!subscriber) return null;
  if (
    newsletter.domain !== "" &&
    newsletter.enabled_features.includes("domains")
  ) {
    return `/subscribers/${subscriber.id}${route}`;
  }
  return `/${newsletter.username}/subscribers/${subscriber.id}${route}`;
};

export const isValidEmail = (email: string): boolean => {
  if (!email || !email.includes("@")) return false;
  if (email.length > 254) return false;
  const emailRegex = /^[a-zA-Z0-9.'_%+\-!]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

export const looksLikeEmail = (email: string): boolean => {
  if (!email) return false;
  const atIndex = email.indexOf("@");
  return atIndex > 0 && atIndex < email.length - 1;
};

export const calculateSubscriptionState = (
  subscriber: BasicSubscriber | undefined,
  auth: SubscriberFacingAuthentication | undefined
): SubscriptionState => {
  if (subscriber === null || subscriber === undefined) {
    if (auth?.status !== "unauthenticated") {
      return "subscribed_elsewhere";
    }
    return "not_subscribed";
  }
  return subscriber.type;
};

export const isOnCustomDomain = (newsletter: Newsletter): boolean =>
  newsletter.domain !== "" && window.location.hostname === newsletter.domain;

export const shouldImmediatelyShowPaymentElement = (
  newsletter: Newsletter
): boolean =>
  (newsletter as any).should_immediately_show_payment_element ||
  window.location.href.includes("immediately_show_payment_element");
